import React from 'react';
import { Autocomplete, Box, Chip, FormLabel, Stack, TextField, Tooltip } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';

const CHIP_COLOR = '#1976d2';

const selectableChipSx = (selected, color = CHIP_COLOR) => ({
  flex: 1,
  justifyContent: 'center',
  bgcolor: selected ? color : 'transparent',
  color: selected ? '#fff' : color,
  border: `1px solid ${color}`,
  fontWeight: 500,
  cursor: 'pointer',
  '&:hover': { bgcolor: selected ? color : `${color}1a` },
});

const BasicDetailsCard = (props) => {
  const { customerList, empList, payerList, programs } = props;
  const sub = props.submitAttempted;

  let pSponsor = [];
  let pManager = [];
  const pCustomer = customerList.map((cust) => ({
    value: !props.edit ? cust.id : cust.cus_id,
    title: !props.edit ? cust.customer_name : cust.cus_name,
  }));
  const pPayers = payerList.map((p) => ({ value: p?.payerDets?.payer_id, title: p?.payerDets?.payer_name }));

  if (props.edit) {
    pSponsor.push({ value: customerList[0].cus_id, title: customerList[0].cus_name });
    pManager = !props.proj_employees
      ? []
      : props.proj_employees.filter((emp) => emp.emp_is_active === true).map((emp) => ({
        value: emp.emp_id,
        title: emp.emp_first_name + ' ' + emp.emp_last_name,
      }));
  } else {
    pSponsor = customerList.filter(({ id }) => id === +props.formVals.pCustomer).map((cust) => ({ value: cust.id, title: cust.customer_name }));
    pManager = empList.filter((emp) => emp.emp_type === 'Manager' || emp.emp_type === 'Admin').map((emp) => ({ value: emp.id, title: emp.employee_name }));
  }

  pSponsor = [{ value: 'No Sponsor', title: 'No Sponsor' }, ...pSponsor];

  const errTitle = sub && !props.formVals.pTitle;
  const errDesc = sub && !props.formVals.pDesc;
  const errPlan = sub && !props.formVals.active_plan_version;
  const errCustomer = sub && !props.formVals.pCustomer;
  const errManager = sub && !props.formVals.pManager;

  return (
    <Stack spacing={2}>
      <TextField
        fullWidth
        size="small"
        variant="outlined"
        label="Project Title"
        value={props.formVals.pTitle}
        onChange={props.handlebasicDetailsChange}
        id="pTitle"
        required
        inputProps={{ maxLength: 100 }}
        error={errTitle}
        helperText={errTitle ? 'Project title is required' : ''}
      />

      <TextField
        fullWidth
        size="small"
        variant="outlined"
        label="Project Description"
        value={props.formVals.pDesc}
        onChange={props.handlebasicDetailsChange}
        id="pDesc"
        required
        multiline
        minRows={2}
        inputProps={{ maxLength: 250 }}
        error={errDesc}
        helperText={errDesc ? 'Project description is required' : ''}
      />

      <TextField
        fullWidth
        size="small"
        variant="outlined"
        label="Project Active Plan Version"
        value={props.formVals.active_plan_version || ''}
        onChange={props.handlebasicDetailsChange}
        id="active_plan_version"
        required
        inputProps={{ maxLength: 20 }}
        error={errPlan}
        helperText={errPlan ? 'Active plan version is required' : ''}
      />

      <Autocomplete
        id="pro_program_id"
        fullWidth
        size="small"
        value={
          programs.find((program) =>
            String(program.program_id) === String(props.formVals.pro_program_id)
          ) || null
        }
        onChange={(event, newValue) => {
          props.handlebasicDetailsChange({
            target: {
              id: 'pro_program_id',
              value: newValue ? newValue.program_id : '',
            },
          });
        }}
        options={programs}
        getOptionLabel={(option) =>
          option.program_name || `Program #${option.program_id}`
        }
        isOptionEqualToValue={(option, value) =>
          String(option.program_id) === String(value.program_id)
        }
        renderInput={(params) => (
          <TextField {...params} label="Program" variant="outlined" />
        )}
      />

      <Autocomplete
        id="pCustomer"
        fullWidth
        size="small"
        value={pCustomer.find((type) => type.value === props.formVals.pCustomer) || null}
        onChange={(event, newValue) => {
          const val = newValue ? pCustomer.find((man) => man.title === newValue.title) : null;
          props.handlebasicDetailsChange({ target: { id: 'pCustomer', value: val ? val.value : '' } });
        }}
        options={pCustomer}
        getOptionLabel={(option) => option.title}
        disabled={props.edit}
        renderInput={(params) => <TextField {...params} label="Customer" variant="outlined" required error={errCustomer} helperText={errCustomer ? 'Customer is required' : ''} />}
      />

      <Autocomplete
        id="pSponsor"
        fullWidth
        size="small"
        value={pSponsor.find((type) => type.value === props.formVals.pSponsor) || null}
        onChange={(event, newValue) => {
          const val = newValue ? pSponsor.find((man) => man.title === newValue.title) : null;
          props.handlebasicDetailsChange({ target: { id: 'pSponsor', value: val ? val.value : 'No Sponsor' } });
        }}
        options={pSponsor}
        getOptionLabel={(option) => option.title}
        disabled={props.edit}
        renderInput={(params) => <TextField {...params} label="Project Sponsor" variant="outlined" />}
      />

      <Autocomplete
        id="pManager"
        fullWidth
        size="small"
        value={pManager.find((type) => type.value === props.formVals.pManager) || null}
        onChange={(event, newValue) => {
          const val = newValue ? pManager.find((man) => man.title === newValue.title) : null;
          props.handlebasicDetailsChange({ target: { id: 'pManager', value: val ? val.value : '' } });
        }}
        options={pManager}
        getOptionLabel={(option) => option.title}
        renderInput={(params) => <TextField {...params} label="Project Manager" variant="outlined" required error={errManager} helperText={errManager ? 'Project manager is required' : ''} />}
      />

      <Box>
        <FormLabel component="legend" sx={{ fontSize: '0.85rem', mb: 0.75, display: 'block' }}>
          Self Pay?
        </FormLabel>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Chip label="Yes" onClick={() => props.handlebasicDetailsChange({ target: { id: 'pSelfPay', value: true } })} sx={selectableChipSx(props.formVals.pSelfPay === true)} />
          <Chip label="No" onClick={() => props.handlebasicDetailsChange({ target: { id: 'pSelfPay', value: false } })} sx={selectableChipSx(props.formVals.pSelfPay === false)} />
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 1.5 }}>
        <Autocomplete
          id="pPrimaryPayer"
          sx={{ flex: 1 }}
          size="small"
          value={pPayers.find((payer) => payer.value === props.formVals.pPrimaryPayer) || null}
          onChange={(event, newValue) => props.handlebasicDetailsChange({ target: { id: 'pPrimaryPayer', value: newValue ? newValue.value : '' } })}
          options={pPayers}
          getOptionLabel={(option) => option.title}
          renderInput={(params) => <TextField {...params} label="Primary Payer" variant="outlined" />}
        />
        <Autocomplete
          id="pSecondaryPayer"
          sx={{ flex: 1 }}
          size="small"
          value={pPayers.find((payer) => payer.value === props.formVals.pSecondaryPayer) || null}
          onChange={(event, newValue) => props.handlebasicDetailsChange({ target: { id: 'pSecondaryPayer', value: newValue ? newValue.value : '' } })}
          options={pPayers}
          getOptionLabel={(option) => option.title}
          renderInput={(params) => <TextField {...params} label="Secondary Payer" variant="outlined" />}
        />
      </Box>

      {['place01char', 'place02char', 'place03char'].map((field) => props.visibilityStatus[field] && (
        <Tooltip title={props.getFieldLabel(field, field)} placement="top" key={field}>
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            label={props.getFieldLabel(field, field)}
            id={field}
            type="text"
            value={props.formVals[field] || ''}
            onChange={props.handlebasicDetailsChange}
            required={props.isFieldRequired(field)}
            error={sub && props.isFieldRequired(field) && !props.formVals[field]}
          />
        </Tooltip>
      ))}

      {['place04date', 'place05date'].map((field) => props.visibilityStatus[field] && (
        <Tooltip title={props.getFieldLabel(field, field)} placement="top" key={field}>
          <Box>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label={props.getFieldLabel(field, field)}
                value={props.formVals[field] ? dayjs(String(props.formVals[field]).replace(/[TZ].*$/, '')) : null}
                onChange={(d) => { if (d && d.isValid()) props.handlebasicDetailsChange({ target: { id: field, value: d.format('YYYY-MM-DD') } }); }}
                slotProps={{ textField: { id: field, size: 'small', fullWidth: true, required: props.isFieldRequired(field), error: sub && props.isFieldRequired(field) && !props.formVals[field] } }}
              />
            </LocalizationProvider>
          </Box>
        </Tooltip>
      ))}

      {['place06number', 'place07number'].map((field) => props.visibilityStatus[field] && (
        <Tooltip title={props.getFieldLabel(field, field)} placement="top" key={field}>
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            label={props.getFieldLabel(field, field)}
            id={field}
            type="number"
            value={props.formVals[field] || ''}
            onChange={props.handlebasicDetailsChange}
            required={props.isFieldRequired(field)}
            error={sub && props.isFieldRequired(field) && !props.formVals[field]}
          />
        </Tooltip>
      ))}
    </Stack>
  );
};

export default BasicDetailsCard;
